import { runReconciliation, resetReconciliationSession } from './cacheReconciliation'
import { onPersistenceFailure } from '@store/collections'

/**
 * 本地缓存 ↔ 云端对账调度器（运行期监控）
 *
 * 触发时机（科学方法：多信号冗余，任一命中即尝试对账）：
 * 1. 启动后延迟（等云快照确认 + 首屏稳定）
 * 2. 周期性（10 分钟，弱网/后台长驻场景的兜底）
 * 3. 网络恢复（online 事件）
 * 4. 应用前台恢复（visibilitychange → visible）
 * 5. 持久化失败感知（onPersistenceFailure：SQLite 写失败累计达阈值 → 主动对账）
 *
 * 对账本身自带前提校验（云快照已确认）+ 冷却 + 次数上限，多信号不会导致风暴。
 */

// 周期性对账间隔（ms）：10 分钟
const PERIODIC_INTERVAL_MS = 10 * 60 * 1000
// 启动后首次对账延迟（ms）：给云快照确认与首屏渲染留时间
const STARTUP_DELAY_MS = 6000
// 持久化失败触发对账的阈值：连续失败达到该次数即主动对账
const PERSIST_FAILURE_THRESHOLD = 3

let started = false
let periodicTimer: ReturnType<typeof setInterval> | null = null
let startupTimer: ReturnType<typeof setTimeout> | null = null
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true

/**
 * 启动运行期对账监控（幂等）。userId 变化（登出/切换）时先 stop 再重启。
 */
export function startCacheReconciliation(userId: string | null): void {
  stopCacheReconciliation()

  if (!userId) return
  started = true

  const attempt = () => {
    if (!started) return
    // 非在线不触发（对账依赖云端清单）
    if (!isOnline) return
    runReconciliation(userId).catch((e) => console.warn('[对账] 周期对账异常:', e))
  }

  // 1. 启动后延迟对账
  startupTimer = setTimeout(attempt, STARTUP_DELAY_MS)

  // 2. 周期对账
  periodicTimer = setInterval(attempt, PERIODIC_INTERVAL_MS)

  // 3. 网络恢复
  const handleOnline = () => {
    isOnline = true
    attempt()
  }
  const handleOffline = () => {
    isOnline = false
  }
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)

  // 4. 前台恢复（移动端切后台再回前台）
  const handleVisibility = () => {
    if (document.visibilityState === 'visible') {
      attempt()
    }
  }
  document.addEventListener('visibilitychange', handleVisibility)

  // 5. 持久化失败感知：累计失败达到阈值 → 主动对账
  //    失败本身意味着"内存有数据、SQLite 可能缺"，是缺口焊死的前兆
  const unsubPersist = onPersistenceFailure((count) => {
    if (count >= PERSIST_FAILURE_THRESHOLD) {
      console.warn(`[对账] 持久化失败累计 ${count} 次，触发主动对账`)
      attempt()
    }
  })

  const cleanup = () => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
    document.removeEventListener('visibilitychange', handleVisibility)
    unsubPersist()
  }
  ;(window as any).__stopCacheReconciliation = cleanup
}

export function stopCacheReconciliation(): void {
  started = false
  if (startupTimer) clearTimeout(startupTimer)
  startupTimer = null
  if (periodicTimer) clearInterval(periodicTimer)
  periodicTimer = null
  if ((window as any).__stopCacheReconciliation) {
    try { (window as any).__stopCacheReconciliation() } catch {}
    ;(window as any).__stopCacheReconciliation = null
  }
  resetReconciliationSession()
}

/**
 * 触发一次立即对账（用户手动"检查缓存完整性"或调试用）
 */
export async function reconcileNow(userId: string): Promise<void> {
  if (!userId) return
  await runReconciliation(userId)
}
